import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, NEXT_ASSESSMENT_URL, WHATSAPP_URL } from "../constants";

export const Nav = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    onNavigate(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#FFFFFF]/80 border-b border-black/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <a
          href="#home"
          onClick={go("#home")}
          data-testid="nav-logo-link"
          className="flex items-center"
        >
          <img
            src="/movementor-logo-color.png"
            alt="Movementor Education"
            className="h-9 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-[#111111] hover:text-[#FE7815] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={NEXT_ASSESSMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-next-assessment-button"
            className="group flex items-center gap-1.5 text-sm font-semibold text-[#111111] px-5 py-2.5 rounded-full border border-[#111111]/15 hover:border-[#FE7815] hover:text-[#FE7815] transition-colors duration-300"
          >
            Explore NEXT
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="flex items-center gap-2 text-sm font-semibold text-white px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FE7815] to-[#F01E1F] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange-500/25 active:translate-y-0 transition-[transform,box-shadow] duration-300"
          >
            <MessageCircle className="w-4 h-4" />
            Talk to a Mentor
          </a>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#111111]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#FFFFFF] border-t border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={go(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-lg font-semibold text-[#111111]"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-2">
                <a
                  href={NEXT_ASSESSMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-next-button"
                  className="flex-1 text-center text-sm font-semibold px-5 py-3 rounded-full border border-[#111111]/15"
                >
                  Explore NEXT
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-whatsapp-button"
                  className="flex-1 text-center text-sm font-semibold text-white px-5 py-3 rounded-full bg-gradient-to-r from-[#FE7815] to-[#F01E1F]"
                >
                  Talk to a Mentor
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
